import { ng } from '@uirouter/angularjs/lib/angular';
import * as angular from 'angular';
import * as _ from 'underscore';
import { HttpService } from '../../../app.common/modules/api/http.service';
import { IImgFileModel } from '../../../app.common/modules/common/directives/models';
import { BlobService } from '../../../app.common/modules/common/services/blob.service';
import { PopupService } from '../../../app.common/modules/common/services/popup.service';
import {
    IPaginationProductsFilter,
    IProductSearchFilter,
    IProductsFetchFilter,
    IProductsItemTypeFilter
} from '../api';
import { RetailerService } from '../api/retailer.service';
import {
    EnumListItem,
    MeasurementListItem,
    PhysicalProduct,
    PhysicalProductItemType,
    PhysicalProductLink,
    PhysicalProductMetadata,
    PhysicalProductsBrand,
    PhysicalProductsPagingResult,
    PhysicalProductVariation,
    PhysicalProductVariationPrice
} from '../core';
import { IRetailerRootScope } from '../core/models';
import { SpecialOffer } from '../model/models';
import { IProductIngredient } from '../model/products/IProductIngredient';
import {
    IBadge, IColumnProductSortSet, IHttpResponseCustom, ILinkListItem, IMeasurementType, IPhysicalProduct,
    IPhysicalProductLink, IPhysicalProductVariation, IProductExtraItemModel, IProductSearchPagingResult, ISpecialOffer,
    IVariationDiscount, IVariationStockLevel
} from './models';
import { IFoodPreference } from './models';

export class ProductsService {
    public static $inject = ['$rootScope', 'http', '$q', '$uibModal', 'retailerService', 'blobService', 'popupService', '$translate'];

    public importType = {
        product: 1,
        category: 2
    };

    private metadata: PhysicalProductMetadata;
    private asc = 'ascending';
    private desc = 'descending';
    private sortSet: IColumnProductSortSet[] = [
        {
            column: 'ID',
            order: this.asc,
            title: this.$translate.instant('creation_date'),
            classSort: 'sort-amount-asc'
        }, {
            column: 'ID',
            order: this.desc,
            title: this.$translate.instant('creation_date'),
            classSort: 'sort-amount-desc'
        }, {
            column: 'Title',
            order: this.asc,
            title: this.$translate.instant('product_name_upper'),
            classSort: 'sort-alpha-asc'
        }, {
            column: 'Title',
            order: this.desc,
            title: this.$translate.instant('product_name_upper'),
            classSort: 'sort-alpha-desc'
        }
    ];

    private linkViewClasses = {
        video: 'fa-youtube-play',
        document: 'fa-file-pdf-o',
        website: 'fa-globe',
        facebook: 'fa-facebook-official',
        twitter: 'fa-twitter',
        instagram: 'fa-instagram',
        pinterest: 'fa-pinterest',
        default: 'fa-external-link-square'
    };

    private linkTypes: EnumListItem[] = null;

    private badges: IBadge[] = [
        { key: 0, name: this.$translate.instant('no_badge'), selected: false, order: 0, color: 'transparent', textcolor: '#585f69' },
        { key: 1, name: this.$translate.instant('new'), selected: false, order: 1, color: '#ffc107', textcolor: '#fff' },
        { key: 2, name: this.$translate.instant('limited_offer'), selected: false, order: 4, color: '#4bac4e', textcolor: '#fff' },
        { key: 4, name: this.$translate.instant('hot_deal'), selected: false, order: 5, color: '#fe9700', textcolor: '#fff' },
        { key: 8, name: this.$translate.instant('sale_ending_soon'), selected: false, order: 7, color: '#6d2077', textcolor: '#fff' },
        { key: 16, name: this.$translate.instant('clearence_sale'), selected: false, order: 8, color: '#f34235', textcolor: '#fff' },
        { key: 32, name: this.$translate.instant('new_collection'), selected: false, order: 2, color: '#ffc107', textcolor: '#fff' },
        { key: 64, name: this.$translate.instant('sale'), selected: false, order: 6, color: '#6d2077', textcolor: '#fff' },
        { key: 128, name: this.$translate.instant('offer'), selected: false, order: 3, color: '#f34235', textcolor: '#fff' },
    ];

    private genders: EnumListItem[] = [
        {ID: this.$rootScope.enums.Gender.Male, Name: this.$translate.instant('male')},
        {ID: this.$rootScope.enums.Gender.Female, Name: this.$translate.instant('female')}
    ];

    private measurementTypes: IMeasurementType[] = [
        { ID: this.$rootScope.enums.MeasurementType.Neck, Name: this.$translate.instant('neck'), SelectText: this.$translate.instant('select_neck_size') },
        { ID: this.$rootScope.enums.MeasurementType.Chest, Name: this.$translate.instant('chest'), SelectText: this.$translate.instant('select_chest_size') },
        { ID: this.$rootScope.enums.MeasurementType.Bust, Name: this.$translate.instant('bust'), SelectText: this.$translate.instant('select_bust_size') },
        { ID: this.$rootScope.enums.MeasurementType.Waist, Name: this.$translate.instant('waist'), SelectText: this.$translate.instant('select_waist_size') },
        { ID: this.$rootScope.enums.MeasurementType.Hips, Name: this.$translate.instant('hips'), SelectText: this.$translate.instant('select_hips_size') },
        { ID: this.$rootScope.enums.MeasurementType.InsideLeg, Name: this.$translate.instant('inside_leg'), SelectText: this.$translate.instant('select_inside_leg_size') },
        { ID: this.$rootScope.enums.MeasurementType.Shoe, Name: this.$translate.instant('shoe'), SelectText: this.$translate.instant('select_shoe_size') },
        { ID: this.$rootScope.enums.MeasurementType.LegLength, Name: this.$translate.instant('leg_length'), SelectText: this.$translate.instant('select_leg_length') },
        { ID: this.$rootScope.enums.MeasurementType.Dress, Name: this.$translate.instant('dress'), SelectText: this.$translate.instant('select_dress_size') }
    ];

    private defaultProduct = {
        title: '',
        brands: [],
        category: null,
        description: '',
        shortDescription: '',
        links: [],
        itemTypeID: null,
        gender: null,
        variations: [],
        error: {},
        measurementItems: {},
        measurementTypeEnable: {},
        measurementTypeVisible: {},
        measurementEnable: false,
        nfcStickers: [],
        allergens: [],
        extras: [],
        ingredientList: [],
        foodPreferences: [],
        originCountry: null,
        preparation: '',
        ingredients: ''
    } as IPhysicalProduct;

    private defaultVariation: IPhysicalProductVariation = {
        id: null,
        color: '',
        size: '',
        sku: '',
        badges: this.getClientBadges(0, false),
        price: '',
        oldPrice: '',
        vat: null,
        insidePrice: null,
        insideVAT: null,
        measurementItemValues: {},
        measurementTypeItem: {},
        imagesID: [],
        ingredients: [],
        imageID: null,
        error: {},
        discount: null,
        specialOffer: null,
        attachment: null,
        isAccessibleOutOfSession: null
    };

    constructor(
        private $rootScope: IRetailerRootScope,
        private http: HttpService,
        private $q: ng.IQService,
        private $uibModal: ng.ui.bootstrap.IModalService,
        private retailerService: RetailerService,
        private blobService: BlobService,
        private popupService: PopupService,
        private $translate: angular.translate.ITranslateService
    ) {

    }

    public async getDefaultProductModel() {
        const product = angular.copy(this.defaultProduct);
        product.allergens = [];
        product.foodPreferences = [];
        product.variations = [];
        const md = await this.getMetadata();
        product.sizeName = md.DefaultVariantFieldsName.SizeName;
        product.colourName = md.DefaultVariantFieldsName.ColourName;
        return product;
    }

    public getDefaultVariationModel(): IPhysicalProductVariation {
        return angular.copy(this.defaultVariation);
    }

    public getProducts(filter: IPaginationProductsFilter | IProductsItemTypeFilter): Promise<PhysicalProductsPagingResult> {
        return this.http.get<PhysicalProductsPagingResult>(this.retailerService.getProducts(), {params: filter});
    }

    public findProducts(filter: IProductSearchFilter): Promise<IProductSearchPagingResult> {
        return this.http.get<IProductSearchPagingResult>(this.retailerService.findProducts(), {params: filter});
    }

    public fetchProducts(filter: IProductsFetchFilter): Promise<IProductSearchPagingResult> {
        return this.http.get<IProductSearchPagingResult>(this.retailerService.fetchProducts(), {params: filter});
    }

    public getImageUrl(imageID: string, width?: number, height?: number): string {
        return this.retailerService.getProductImageUrl(imageID, width, height || width);
    }

    public getSort(column?: string, order?: string): IColumnProductSortSet {
        if (column && order) {
            return _.findWhere(this.sortSet, {column, order});
        } else {
            return this.sortSet[1];
        }
    }

    public getSortSet(): IColumnProductSortSet[] {
        return this.sortSet;
    }

    public uploadImage(file: IImgFileModel): angular.IPromise<string> {
        if (!file) {
            return this.$q.when(null as string);
        }
        const fd = new FormData();
        fd.append('FileImage', this.blobService.dataUriToBlob(file.dataUri), file.name);
        return this.http.post<string>(this.retailerService.variationImage(), fd, {
            transformRequest: angular.identity,
            headers: {'Content-Type': undefined}
        });
    }

    public deleteImage(imageID: string): Promise<boolean> {
        return this.http.delete<boolean>(this.retailerService.variationImage(imageID));
    }

    public exportCsvProduct = (query: {searchTerm: string; sortColumn: string; sortOrder: string}): Promise<IHttpResponseCustom<string>> => {
        return this.http.getAllResponse(this.retailerService.exportCsvProduct(), {
            params: query,
            responseType: 'text'
        });
    }

    public copyImage(imagesID: string[]): Promise<string[]> {
        return this.http.post<string[]>(this.retailerService.variationImageCopy(), imagesID);
    }

    public updateProduct(productID: string, model: IPhysicalProduct): Promise<any> {
        return this.http.put(this.retailerService.product(productID), this.toServerProductModel(model));
    }

    public createProduct(model: IPhysicalProduct): Promise<any> {
        return this.http.post(this.retailerService.product(), this.toServerProductModel(model));
    }

    public archiveProduct(productID: string): Promise<number> {
        return this.http.post<number>(this.retailerService.archiveProduct(productID), null);
    }

    public restoreProduct(productID: string): Promise<number> {
        return this.http.post<number>(this.retailerService.restoreProduct(productID), null);
    }

    public restoreVariation(productID: string, variationID: string): Promise<number> {
        return this.http.post<number>(this.retailerService.restoreProduct(productID, variationID), null);
    }

    public getLinkTypes = async () => {
        if (_.isArray(this.linkTypes)) {
            return this.$q.when(this.linkTypes);
        } else {
            const types = await this.http.get<EnumListItem[]>(this.retailerService.productLinkTypes()) as ILinkListItem[];
            _.forEach(types, (type) => {
                const viewClass = this.linkViewClasses[type.Name.toLowerCase()];
                type.viewClass = viewClass ? viewClass : this.linkViewClasses.default;
            });
            this.linkTypes = types;
            return this.$q.when(this.linkTypes);
        }

    }

    public getGenders(): EnumListItem[] {
        return angular.copy(this.genders);
    }

    public isExistProducts(state: number): Promise<boolean> {
        return this.http.get<boolean>(this.retailerService.existProducts(state));
    }

    public getTwinsVariations(productID: string, variationID: string): Promise<PhysicalProductVariation[]> {
        return this.http.get<PhysicalProductVariation[]>(this.retailerService.productVariationTwins(productID, variationID));
    }

    public getItemTypes(categoryID: number, gender: number): Promise<PhysicalProductItemType[]> {
        return this.http.get<PhysicalProductItemType[]>(this.retailerService.productItemTypes(categoryID, gender));
    }

    public getMeasurements(gender: number) {
        return this.http.get<MeasurementListItem[]>(this.retailerService.measurements(gender));
    }

    public getSmlMeasurements(gender: number) {
        return this.http.get<MeasurementListItem[]>(this.retailerService.smlMeasurements(gender));
    }

    public getMeasurementTypes(): IMeasurementType[] {
        return angular.copy(this.measurementTypes) as IMeasurementType[];
    }

    public updatePrice(productID: string, model: PhysicalProductVariationPrice): Promise<string> {
        return this.http.put<string>(this.retailerService.productPrice(productID), model);
    }

    public updatePriceWithVat(productID: string, model: PhysicalProductVariationPrice): Promise<string> {
        return this.http.put<string>(this.retailerService.productPriceWithVat(productID), model);
    }

    public updateDiscount(productID: string, model: IVariationDiscount): Promise<any> {
        return this.http.put(this.retailerService.productDiscount(productID), model);
    }

    public updateStockLevel(productID: string, model: IVariationStockLevel): Promise<any> {
        return this.http.put(this.retailerService.productStockQuantity(productID), model);
    }

    public showSaveChangesDialog() {
        const title = this.$translate.instant('save_changes_in_a_product_copy');
        const message = this.$translate.instant('save_changes_in_a_product_copy_confirm');
        return this.popupService.confirm({
            title,
            content: message
        });
    }

    public getProductModel = async (productID: string, edit: boolean) => {
        const serverModel = await this.http.get<PhysicalProduct>(this.retailerService.product(productID));
        const imagesID = [] as string[];
        if (!edit) {
            serverModel.NfcStickers = [];
            _.forEach(serverModel.Variations, (variation) => {
                _.forEach(variation.ImagesID, (id) => {
                    imagesID.push(id);
                });
            });
        }
        if (_.isEmpty(imagesID)) {
            return this.toClientProductModel(serverModel, imagesID, edit);
        } else {
            const exchange = await this.copyImage(imagesID);
            return this.toClientProductModel(serverModel, exchange, edit);
        }
    }

    public getClientBadges(number: number, onlySelected: boolean): IBadge[] {
        let badges = this.getBadges();
        if (number === 0) {
            badges[0].selected = true;

        } else {
            const numberBit = this.getBitStringByNumber(number);

            for (let c = 0; c < numberBit.length; c++) {
                badges[c + 1].selected = numberBit[c] === '1';
            }
        }
        badges = _.sortBy(badges, 'order');
        if (onlySelected) {
            badges = _.where(badges, {selected: true});
        }
        return badges;
    }

    public toServerProductModel(model: IPhysicalProduct): PhysicalProduct {
        const brand = model.brands[0];
        return {
            Title: model.title,
            NfcStickers: model.nfcStickers,
            VerificationRequired: model.verificationRequired,
            IsInformation: model.isInformation,
            Description: model.description,
            ShortDescription: model.shortDescription,
            Brand: brand ? {
                ID: brand.ID,
                Name: brand.Name,
                Status: brand.Status,
            } as PhysicalProductsBrand : null,
            Gender: model.genderItem ? (model.genderItem.ID === 1 ? PhysicalProduct.GenderEnum.NUMBER_1 : PhysicalProduct.GenderEnum.NUMBER_2) : null,
            ClassificationCategory: model.category ? {
                ID: model.category.ID,
                Name: model.category.Name,
                FullName: model.category.FullName
            } : null,
            Links: _.map(model.links, this.toServerLink),
            // Tags: <PhysicalProductTagListItem[]>model.tags,
            ItemTypeID: model.itemType ? model.itemType.ID : null,
            Variations: _.map(model.variations, (variation) => {
                if (model.measurementEnable) {
                    variation.size = null;
                } else {
                    variation.measurementTypeItem = {};
                }
                return this.toServerVariation(variation);
            }),
            Allergens: model.allergens && model.allergens.length > 0 ? _.map(_.filter(model.allergens, (allergen) => {
                return allergen.IsSelected === true;
            }), (allergen) => {
                return allergen.ID;
            }) : null,
            FoodPreferences: model.foodPreferences && model.foodPreferences.length > 0 ? _.map(_.filter(model.foodPreferences, (allergen) => {
                return allergen.IsSelected === true;
            }), (allergen) => {
                return allergen.ID;
            }) : null,
            Extras: model.extras,
            IngredientList: model.ingredientList,
            Preparation: model.preparation,
            Ingredients: model.ingredients,
            Source: model.originCountry ? model.originCountry.ID : null,
            PreOrder: model.preOrder
        };
    }

    public uploadImportFile(file: File, importType: number): Promise<string> {
        if (!file) {
            return this.$q.when(null);
        }
        const url = importType === this.importType.category ? this.retailerService.categoryMappingImport() : this.retailerService.productsImport();
        const fd = new FormData();
        fd.append('file', file, file.name);
        return this.http.post<string>(url, fd, {
            transformRequest: angular.identity,
            headers: {'Content-Type': undefined}
        });
    }

    public isExistCategoryMapping(): Promise<boolean> {
        return this.http.get<boolean>(this.retailerService.categoryMappingExist());
    }

    public isProductsImportQueued(): Promise<boolean> {
        return this.http.get<boolean>(this.retailerService.productsImportQueued());
    }

    public productsImport() {
        return this.$uibModal.open({
            component: 'productsImport',
            resolve: {},
            size: 'sm',
        }).result;
    }

    public async getMetadata() {
        if (!this.metadata) {
            this.metadata = await this.http.get<PhysicalProductMetadata>(this.retailerService.productsMetadata());
        }
        return this.metadata;
    }

    public async GetCountryOrigins(): Promise<EnumListItem[]> {
        return await this.http.get<EnumListItem[]>(this.retailerService.countryOrigins());
    }

    public async getCategoryFieldNames(categoryID: number) {
        const md = await this.getMetadata();
        const fieldNames = md.DefaultVariantFieldsName;
        if (categoryID) {
            const search = md.CategoryVariantFieldsNames[categoryID];
            if (search) {
                return search;
            }
        }
        return fieldNames;
    }

    public addAttachment(file: File): Promise<string> {
        const formData = new FormData();
        formData.append('File', file);
        return this.http.post<string>(this.retailerService.attachment(), formData, {
            transformRequest: angular.identity,
            headers: {'Content-Type': undefined}
        });
    }

    public deleteAttachment(id: string): Promise<boolean> {
        return this.http.delete<boolean>(this.retailerService.attachment(id));
    }

    public getAttachment(id: string): Promise<any> {
        return this.http.get(this.retailerService.attachment(id), { responseType: 'arraybuffer' });
    }

    private getBadges(): IBadge[] {
        return _.sortBy(angular.copy(this.badges), 'key');
    }

    private getBitStringByNumber(number: number): string {
        // needed since toString does not zero fill on left
        let template = '';
        for (let c = 1; c < _.size(this.badges); c++) {
            template += '0';
        }
        // toString converts a number to a bitstring
        const bit = number.toString(2).split('').reverse().join('');
        return bit + template.substring(0, template.length - bit.length);
    }

    private toClientVariation(serverVariation: PhysicalProductVariation, exchangeImages: string[], edit: boolean): IPhysicalProductVariation {
        const variation = this.getDefaultVariationModel();
        variation.color = serverVariation.Color;
        variation.size = serverVariation.Size;
        variation.sku = serverVariation.SKU;
        variation.price = serverVariation.Price;
        variation.oldPrice = serverVariation.OldPrice;
        variation.discount = serverVariation.Discount;
        variation.vat = serverVariation.VAT;
        variation.insidePrice = serverVariation.InsidePrice;
        variation.insideVAT = serverVariation.InsideVAT;
        variation.isHot = serverVariation.IsHot;
        variation.preparationTime = serverVariation.PreparationTime;
        variation.attachment = serverVariation.Attachment;
        variation.isAccessibleOutOfSession = serverVariation.IsAccessibleOutOfSession;

        if (!serverVariation.Price) {
            serverVariation.Ingredients.forEach((ingredient) => ingredient.Price = null);
        }

        if (!serverVariation.InsidePrice) {
            serverVariation.Ingredients.forEach((ingredient) => ingredient.InsidePrice = null);
        }

        variation.ingredients = serverVariation.Ingredients;
        variation.measurementItemValues[this.$rootScope.enums.MeasurementType.Neck] = serverVariation.NeckSize;
        variation.measurementItemValues[this.$rootScope.enums.MeasurementType.Chest] = serverVariation.ChestSize;
        variation.measurementItemValues[this.$rootScope.enums.MeasurementType.Bust] = serverVariation.BustSize;
        variation.measurementItemValues[this.$rootScope.enums.MeasurementType.Waist] = serverVariation.WaistSize;
        variation.measurementItemValues[this.$rootScope.enums.MeasurementType.Hips] = serverVariation.HipsSize;
        variation.measurementItemValues[this.$rootScope.enums.MeasurementType.InsideLeg] = serverVariation.InsideLegSize;
        variation.measurementItemValues[this.$rootScope.enums.MeasurementType.Shoe] = serverVariation.ShoeSize;
        variation.measurementItemValues[this.$rootScope.enums.MeasurementType.LegLength] = serverVariation.LegLengthSize;
        variation.measurementItemValues[this.$rootScope.enums.MeasurementType.Dress] = serverVariation.DressSize;

        variation.imagesID = edit ? serverVariation.ImagesID : _.map(serverVariation.ImagesID, (id) => exchangeImages[id]);
        variation.badges = this.getClientBadges(serverVariation.Badges, false);
        variation.id = edit ? serverVariation.ID : null;
        variation.specialOffer = serverVariation.SpecialOffer as ISpecialOffer;
        if (variation.specialOffer && variation.specialOffer.OfferValidFrom) {
            variation.specialOffer.OfferValidFrom = new Date(variation.specialOffer.OfferValidFrom.toString());
        }
        if (variation.specialOffer && variation.specialOffer.OfferValidTo) {
            variation.specialOffer.OfferValidTo = new Date(variation.specialOffer.OfferValidTo.toString());
        }
        variation.stockQuantity = serverVariation.StockQuantity;
        return variation;
    }

    private async toClientProductModel(serverProduct: PhysicalProduct, exchangeImages: string[], edit: boolean): Promise<IPhysicalProduct> {
        const product = angular.copy(this.defaultProduct);
        product.title = serverProduct.Title;
        product.nfcStickers = serverProduct.NfcStickers;
        product.verificationRequired = serverProduct.VerificationRequired;
        product.isInformation = serverProduct.IsInformation;
        product.brands = serverProduct.Brand ? [serverProduct.Brand] : [];
        product.category = serverProduct.ClassificationCategory;
        product.description = serverProduct.Description;
        product.shortDescription = serverProduct.ShortDescription;
        product.draft = serverProduct.Draft;
        product.links = serverProduct.Links as IPhysicalProductLink[];
        product.itemTypeID = serverProduct.ItemTypeID;
        product.gender = serverProduct.Gender;
        product.variations = _.map(serverProduct.Variations, (variation) => {
            return this.toClientVariation(variation, exchangeImages, edit);
        });
        const fieldNames = await this.getCategoryFieldNames(product.category ? product.category.ID : null);
        product.sizeName = fieldNames.SizeName;
        product.colourName = fieldNames.ColourName;
        if (serverProduct.Allergens) {
            product.allergens = _.map(serverProduct.Allergens, (id) => {
                const all: IFoodPreference = {ID: id, IsSelected: true};
                return all;
            });
        }
        if (serverProduct.FoodPreferences) {
            product.foodPreferences = _.map(serverProduct.FoodPreferences, (id) => {
                const pref: IFoodPreference = {ID: id, IsSelected: true};
                return pref;
            });
        }
        product.extras = _.map(serverProduct.Extras, (extr) => {
            const extra: IProductExtraItemModel = {
                ID: extr.ID,
                ImageID: extr.ImageID,
                Title: extr.Title,
                Price: extr.Price,
                MaxAmount: extr.MaxAmount,
                CurrencySign: extr.CurrencySign,
                QuantityError : null
            };
            return extra;
        } );
        product.ingredientList = _.map(serverProduct.IngredientList, (ingredient) => {
           const item: IProductIngredient = {
               ID: ingredient.ID,
               MinQuantity: ingredient.MinQuantity,
               Title: ingredient.Title
           };
           return item;
        });
        product.ingredients = serverProduct.Ingredients;
        product.preparation = serverProduct.Preparation;
        if (serverProduct.Source) {
            product.originCountry = {ID: serverProduct.Source};
        }
        product.preOrder = serverProduct.PreOrder;

        return product;
    }

    private getServerBadges(badges: IBadge[]): PhysicalProductVariation.BadgesEnum {
        let number = 0;
        _.forEach(badges, (badge) => {
            if (badge.selected) {
                number += badge.key;
            }
        });
        return number as PhysicalProductVariation.BadgesEnum;
    }

    private toServerLink(link: IPhysicalProductLink): PhysicalProductLink {
        return {
            Type: link.type.ID as PhysicalProductLink.TypeEnum,
            Title: link.Title,
            Url: link.Url
        };
    }

    private toServerVariation = (variation): PhysicalProductVariation => {
        const measurementType = this.$rootScope.enums.MeasurementType;
        return {
            ID: variation.id,
            Size: variation.size,
            Color: variation.color,
            ImagesID: variation.imagesID,
            Price: variation.price,
            OldPrice: variation.oldPrice,
            SKU: variation.sku,
            Badges: this.getServerBadges(variation.badges),
            Discount: variation.discount,
            VAT: variation.vat,
            InsidePrice: variation.insidePrice,
            InsideVAT: variation.insideVAT,
            IsHot: variation.isHot,
            PreparationTime: variation.preparationTime,
            Ingredients: variation.ingredients,
            SpecialOffer: this.toServerSpecialOffer(variation.specialOffer),
            StockQuantity: variation.stockQuantity,

            NeckSize: variation.measurementTypeItem[measurementType.Neck] ? variation.measurementTypeItem[measurementType.Neck].Value : null,
            ChestSize: variation.measurementTypeItem[measurementType.Chest] ? variation.measurementTypeItem[measurementType.Chest].Value : null,
            BustSize: variation.measurementTypeItem[measurementType.Bust] ? variation.measurementTypeItem[measurementType.Bust].Value : null,
            WaistSize: variation.measurementTypeItem[measurementType.Waist] ? variation.measurementTypeItem[measurementType.Waist].Value : null,
            HipsSize: variation.measurementTypeItem[measurementType.Hips] ? variation.measurementTypeItem[measurementType.Hips].Value : null,
            InsideLegSize: variation.measurementTypeItem[measurementType.InsideLeg] ? variation.measurementTypeItem[measurementType.InsideLeg].Value : null,
            ShoeSize: variation.measurementTypeItem[measurementType.Shoe] ? variation.measurementTypeItem[measurementType.Shoe].Value : null,
            LegLengthSize: variation.measurementTypeItem[measurementType.LegLength] ? variation.measurementTypeItem[measurementType.LegLength].Value : null,
            DressSize: variation.measurementTypeItem[measurementType.Dress] ? variation.measurementTypeItem[measurementType.Dress].Value : null,
            Attachment: variation.attachment ? variation.attachment : null,
            IsAccessibleOutOfSession: variation.isAccessibleOutOfSession
        };
    }

    private toServerSpecialOffer(specialOffer: ISpecialOffer): SpecialOffer | null {
        if (specialOffer) {
            const copy = angular.copy(specialOffer);
            delete copy.error;
            return copy as SpecialOffer;
        }
        return null;
    }
}

angular
    .module('naut')
    .service('productsService', ProductsService);



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/app.retailer/modules/products/products.service.ts